import React, { useState, useEffect } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { EmailSendingAddress, EmailLayout, EmailTemplate, initializeEmailTemplateData } from '../../email/EmailTypes';
import { Division } from '../../environments/EnvironmentTypes';
import { GroupType } from '../../users/UserTypes';
import { saveData } from 'services/api/saveData';
import { useFetchData } from 'services/api/useFetchData';
import { usePackageFeature } from 'services/permissions/permissionChecks';
import { emailTemplateLibraryDefaultsByType } from './emailTemplateLibraryDefaults';
import EmailTemplateFormBaseFields from './EmailTemplateFormBaseFields';
import EmailTemplateFormMessageFields from './EmailTemplateFormMessageFields';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useParams } from 'react-router-dom';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import EmailLayoutExample from './EmailLayoutExample';
import '../../../style/scss/forms.scss';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/detailpage.scss';

const EmailTemplatePage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id, templateType } = useParams<{ id?: string; templateType?: string; }>();
    const { activeFeatures } = useAuthContext();
    const { setFloatingAlert, setErrorMessages } = useGlobalContext()
    const { selectedDivision: settingsDivision, isEnvironmentSelected } = useSettingsDivision();
    const [data, setData] = useState<EmailTemplate | null>(null);
    const [groups, setGroups] = useState<GroupType[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [sendingAddresses, setSendingAddresses] = useState<EmailSendingAddress[]>([]);
    const [filteredSendingAddresses, setFilteredSendingAddresses] = useState<EmailSendingAddress[]>([]);
    const [emailLayouts, setEmailLayouts] = useState<EmailLayout[]>([]);
    const [selectedSendTo, setSelectedSendTo] = useState<string[]>(data?.send_to ?? []);
    const [loading, setLoading] = useState<'idle' | 'show-loader' | 'success'>('idle');
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);
    const [cancelSource, setCancelSource] = useState<CancelTokenSource | null>(null);  
    const shouldFetchGroups = activeFeatures.length > 0 && activeFeatures.includes('groups');
    const shouldFetchEmailLayouts = activeFeatures.length > 0 && activeFeatures.includes('email_layouts');
    const shouldFetchDivisions = activeFeatures.length > 0 && activeFeatures.includes('divisions');
    const enableAutoMail = usePackageFeature('extended_emails');

    // Fetch the data
    const { response: fetchedSendingAddresses } = useFetchData({ apiUrl: 'get_email_sending_address_list' });
    const { response: fetchedCurrentData } = useFetchData({ apiUrl: id ? `get_email_template/${id}` : '', useGuard: true, guardValue: id });

    // Fetch the groups when the feature is active
    const { response: fetchedGroups } = useFetchData({ 
        apiUrl: shouldFetchGroups ? 'get_group_list' : '',
        useGuard: true,
        guardValue: shouldFetchGroups
    });

    // Fetch the email layouts when the feature is active
    const { response: fetchedEmailLayouts } = useFetchData({ 
        apiUrl: shouldFetchEmailLayouts ? 'get_email_layout_list' : '',
        useGuard: true,
        guardValue: shouldFetchEmailLayouts
    });

    // Fetch the divisions when the feature is active
    const { response: fetchedDivisions } = useFetchData({ 
        apiUrl: shouldFetchDivisions ? 'get_division_list' : '',
        useGuard: true,
        guardValue: shouldFetchDivisions
    });

    // Show loader
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;

        // Show loader after 250 ms
        timer = setTimeout(() => {
            if (data === null) {
                setLoading('show-loader');
            }
        }, 250);

        // Clear timer and request on unmount
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [data]);

    // Set the fetched data and reveal the modal
    useEffect(() => {
        // Check if all fetches are received, otherwise return
        const allFetched =
            fetchedSendingAddresses &&
            (id == null || fetchedCurrentData) &&
            (!activeFeatures.includes('groups') || fetchedGroups) &&
            (!activeFeatures.includes('email_layouts') || fetchedEmailLayouts) &&
            (!activeFeatures.includes('divisions') || fetchedDivisions);
        if (!allFetched) return;

        // Stop the loader
        setLoading('success');

        // If an item id is given, set the current data
        if (id != null && fetchedCurrentData) {
            // Get the prefix from the from email
            const prefix = fetchedCurrentData.from_email.split('@')[0];

            // Set the fetched data
            setData({
                ...fetchedCurrentData, 
                from_prefix: prefix,
            });

            // Set the send to
            setSelectedSendTo(fetchedCurrentData.send_to);

        // Otherwise initialize as a new email template with the base template data
        } else {
            // Determine the email template defaults by template type
            const baseData = initializeEmailTemplateData(templateType);
            const enrichedData = emailTemplateLibraryDefaultsByType(baseData);

            // Show the recipients of the library defaults in the send to field
            setSelectedSendTo(enrichedData.send_to ?? []);

            // Prefill the example subject and body of the template type when they exist
            const exampleSubject = templateType ? t(`email.email_template.template_type.${templateType}.example_subject`, { defaultValue: '' }) : '';
            const exampleBody = templateType ? t(`email.email_template.template_type.${templateType}.example_body`, { defaultValue: '' }) : '';
            if (exampleSubject) enrichedData.subject = exampleSubject;
            if (exampleBody) enrichedData.body = exampleBody;

            // Prefill the division with the division selected in the settings menu
            const divisionDefault = !isEnvironmentSelected && settingsDivision ? { division: settingsDivision.id } : {};

            setData({ ...enrichedData, ...divisionDefault, name: templateType && templateType !== 'blank_template' ? t(`email.email_template.template_type.${templateType}.name`) : '' });
        }

        // Set the dropdown data
        setSendingAddresses(fetchedSendingAddresses.results);
        setFilteredSendingAddresses(fetchedSendingAddresses.results.filter((address: EmailSendingAddress) => !address.deleted) || []);

        // Set the groups
        if (activeFeatures.includes('groups')) {
            setGroups(fetchedGroups.results);
        }

        // Set the email layouts
        if (activeFeatures.includes('email_layouts')) {
            setEmailLayouts(fetchedEmailLayouts.results);
        }

        // Set the divisions
        if (activeFeatures.includes('divisions')) {
            setDivisions(fetchedDivisions.results);
        }
    }, [id, fetchedGroups, fetchedDivisions, fetchedSendingAddresses, fetchedEmailLayouts, fetchedCurrentData]);

    // Helper function to convert files
    const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    // Handle submit
    const handleSubmit = async () => {
        try {
            setButtonLoader(true);

            // Create the cancel token source
            const source = axios.CancelToken.source();
            setCancelSource(source);

            // Configure the api request
            const apiUrl = id ? `patch_email_template/${id}` : 'post_email_template';
            const method = id ? 'patch' : 'post';

            const submitData: any = { ...data };

            // Add new attachments as base64
            if (Array.isArray(submitData.new_default_attachments) && submitData.new_default_attachments.length > 0) {
                submitData.new_default_attachments = await Promise.all(
                    submitData.new_default_attachments.map(async (attachment: any) => ({
                        filename: attachment.filename,
                        file_base64: await fileToBase64(attachment.file),
                    }))
                );
            } else {
                delete submitData.new_default_attachments;
            }
          
            // Save the data in the backend
            const response = await saveData({ 
                apiUrl, 
                method, 
                data: submitData, 
                source 
            });

            // Handle successful response
            if (response && (response.status === 200 || response.status === 201)) {
                setButtonLoader(false);
                setFloatingAlert({ 'type': 'success' });
                setErrorMessages({});

                // Redirect back to the email settings
                history.push('/settings/features/email/email-templates')
            }
        } catch (error: any) {
            if (error.response && error.response.status === 400) {
                setErrorMessages(error.response.data);
            } else {
                setFloatingAlert({ type: 'danger', message: 'validation.general.save_failed' })
            }

            setButtonLoader(false);
        }
    };

    // Handle close
    const handleClose = () => {
        cancelSource?.cancel();
        setErrorMessages({});
        setButtonLoader(false);

        // Redirect back to the email settings
        history.push('/settings/features/email/email-templates')
    };

    // Get the header title
    const getHeaderTitle = (): string => {
        let title: string;

        // Get the template type
        const type = templateType ?? data?.template_type;

        // Configure the header title
        if (type && type !== 'blank_template') {
            title = id
                ? t('email.email_template.edit_template_from_type', { templateType: t(`email.email_template.template_type.${type}.name`) })
                : t('email.email_template.add_template_from_type', { templateType: t(`email.email_template.template_type.${type}.name`) });
        } else {
            title = id ? t('email.email_template.edit_template') : t('email.email_template.add_template');
        }

        return title;
    };

    return (
        <div className='detailpage email-template-page container-filled'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <span className='back-button'
                          onClick={() => { id 
                            ? history.push('/settings/features/email/email-templates') 
                            : history.push('/settings/features/email/template-library/')
                        }}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    <h2 className='fixed-title'>
                        {getHeaderTitle()}
                    </h2>
                </div>
                <div className='button-container right-align'>

                </div>
            </div>
            <div className='detailpage-content'>
                <div>
                    <EmailTemplateFormBaseFields
                        data={data}
                        loading={loading}
                        divisions={divisions}
                        sendingAddresses={sendingAddresses}
                        layouts={emailLayouts}
                        templateType={templateType}
                        setData={setData}
                        setSelectedSendTo={setSelectedSendTo}
                        setFilteredSendingAddresses={setFilteredSendingAddresses}
                    />
                    <EmailTemplateFormMessageFields
                        data={data}
                        loading={loading}
                        groups={groups}
                        sendingAddresses={sendingAddresses}
                        filteredSendingAddresses={filteredSendingAddresses}
                        selectedSendTo={selectedSendTo}
                        setData={setData}
                        setSelectedSendTo={setSelectedSendTo}
                    />
                </div>
                <div>
                    <EmailLayoutExample
                        layoutId={data?.layout ?? undefined} 
                        emailData={{
                            send_to: data?.send_to ?? undefined,
                            specific_email: data?.send_to_specific_email ?? undefined,
                            bcc_recipient: data?.default_bcc_recipient ?? undefined,
                            from_email: data?.from_email ?? undefined,
                            reply_to: data?.reply_to ?? undefined,
                            subject: data?.subject ?? undefined,
                            body: data?.body ?? undefined
                        }}
                    />
                </div>
            </div>
            <div className='footer-fixed'>
                <div className='container-filled'>
                    <SecondaryButton
                        onClick={handleClose} 
                        label="general.cancel"
                        size="small"
                    />
                    <PrimaryButton
                        onClick={handleSubmit}
                        label="general.save"
                        disabled={{
                            disabled: !enableAutoMail && data?.send_method === 'automatically' && data?.connected_feature !== 'tickets' ? true : false,
                            message: 'email.email_template.automatic_email_upgrade_message'
                        }}
                        size="small"
                        loading={buttonLoader} 
                    />
                </div>
            </div>
        </div>
    );
};

export default EmailTemplatePage;